import { computed, ref, watch } from 'vue';

export function splitDateTime(value: string | null | undefined) {
    // Empty value leaves both date and time blank instead of showing 00:00
    if (!value) return { date: null as string | null, hour: null as number | null, minute: null as number | null };
    const [date, time] = value.split('T');
    const [hour, minute] = (time ?? '00:00').split(':').map(Number);
    return { date, hour, minute };
}

export function combineDateTime(date: string | null, hour: number | null, minute: number | null) {
    if (!date) return null;
    return `${date}T${(hour ?? 0).toString().padStart(2, '0')}:${(minute ?? 0).toString().padStart(2, '0')}:00`;
}

/** Normalizes an API date to "yyyy-MM-ddTHH:mm:00", so it compares as a plain string with the picker values. */
export function normalizeDateTime(value: string | null | undefined) {
    const split = splitDateTime(value);
    return combineDateTime(split.date, split.hour, split.minute);
}

/** Date, hour and minute picker values for one event date, kept in sync with the event. */
export function useDateTimeField(source: () => string | null | undefined) {
    const date = ref<string | null>(null);
    const hour = ref<number | null>(null);
    const minute = ref<number | null>(null);

    watch(
        source,
        (value) => {
            const split = splitDateTime(value);
            date.value = split.date;
            hour.value = split.hour;
            minute.value = split.minute;
        },
        { immediate: true }
    );

    // Only saved when date and time are both set or both empty, so a half-filled field isn't stored as 00:00 or dropped
    // Hour and minute are cleared one at a time, so only both set (or both empty) counts as a time (or none)
    const hasTime = computed(() => hour.value != null && minute.value != null);
    const isComplete = computed(() => (date.value ? hasTime.value : hour.value == null && minute.value == null));
    const value = computed(() => (hasTime.value ? combineDateTime(date.value, hour.value, minute.value) : null));

    // Flags the empty side of a half-filled field
    const isDateMissing = computed(() => !date.value && (hour.value != null || minute.value != null));
    const isTimeMissing = computed(() => !!date.value && !hasTime.value);

    return { date, hour, minute, value, isComplete, isDateMissing, isTimeMissing };
}
